import type { FC } from 'react'

import styled from '@emotion/styled'

import Badge from '@segment/matcha/components/Badge'

import HeroBase, * as Hero from '@segment/matcha/recipes/Hero'
import AuthorCardBase, * as AuthorCard from '@segment/matcha/recipes/AuthorCard'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import type { Lesson, Bio } from '@segment/api/routes/growthMasters/queries'

import Video from './Video'
import ShareLesson from './ShareLesson'

interface HeroProps {
  slug: string
  title: string
  description: Lesson[`shortDescription`]
  badges: Lesson[`badges`]
  speaker: Bio
  videoUrl: Lesson[`videoUrl`]
  videoThumbnail: Lesson[`videoCoverUrl`]
}

const LessonPageHero: FC<HeroProps> = ({ slug, title, description, badges, speaker, videoUrl, videoThumbnail }) => {
  return (
    <Wrapper>
      <HeroBase variant="2Col" copyVariant="narrow">
        <StyledHeroCopy>
          {badges?.map((topic, key) => (
            <StyledBadge key={topic + key} variant="outlined">
              {topic}
            </StyledBadge>
          ))}
          <CopyText.Heading tag="h1" variant="h2">
            {title}
          </CopyText.Heading>
          <AuthorCardBase variant="transparent">
            <AuthorCard.Avatar alt={speaker.name} src={speaker.headshotUrl} />
            <AuthorCard.Body>
              <AuthorCard.Name>{speaker.name}</AuthorCard.Name>
              <AuthorCard.Description>
                {speaker.title} {speaker.company}
              </AuthorCard.Description>
            </AuthorCard.Body>
          </AuthorCardBase>
          <ShareLesson slug={slug} />
        </StyledHeroCopy>
        <StyledHeroContents>
          <Video url={videoUrl} cover={videoThumbnail} />
        </StyledHeroContents>
      </HeroBase>
      <StyledSectionBase variant="centered">
        <Section.Copy>
          <CopyTextBase centered={true}>
            <CopyText.Description tag="h2" variant="pLarge">
              {description}
            </CopyText.Description>
          </CopyTextBase>
        </Section.Copy>
      </StyledSectionBase>
    </Wrapper>
  )
}

const Wrapper = styled.div``

const StyledSectionBase = styled(SectionBase)`
  ${Section.Wrapper} {
    padding-top: 0;
    padding-bottom: 0;
    gap: 0;
  }
`

const StyledBadge = styled(Badge)`
  margin-right: 8px;
  margin-bottom: 8px;
`

const StyledHeroCopy = styled(Hero.Copy)`
  align-self: flex-start;
  text-align: left;
`

const StyledHeroContents = styled(Hero.Contents)`
  max-width: 640px;
  margin: 0 auto;
`

export default LessonPageHero
